import { useMemo } from 'react';

export type Pose =
  | 'idle'
  | 'cheer'
  | 'think'
  | 'still'
  | 'asleep'
  | 'open'
  | 'chomp'
  | 'no'
  | 'sit'
  | 'walk'
  | 'hold';

/** Shizi the lion: one flat SVG, poses driven by CSS (see styles.css). Reads at 24px.
 * `asleep`: eyes shut, a zzz; `open`: mouth open, waiting to be fed; `chomp`: a bite;
 * `no`: a head shake; `sit`, `walk`, `hold` need the body and imply `variant="full"`.
 *
 * `variant` decides whether he has a body (visual plan §1.3). Head-only is the default because
 * that is what the avatars, badges and loaders around the app want; the game boards where he
 * has to *be* somewhere — in a boat, at a table, on a rock, crossing a bridge — ask for
 * `full`, which extends the viewBox downwards and keeps the head's geometry untouched. */
export function Shizi({
  size = 120,
  pose = 'idle',
  variant,
  facing = 'right',
  className = '',
}: {
  size?: number;
  pose?: Pose;
  variant?: 'head' | 'full';
  /** Mirrors the whole drawing; the poses are unaffected. */
  facing?: 'left' | 'right';
  className?: string;
}) {
  const full = variant === 'full' || pose === 'sit' || pose === 'walk' || pose === 'hold';
  const height = full ? 176 : 120;
  const mane = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const a = ((i * 30 - 90) * Math.PI) / 180;
        return { x: 60 + 44 * Math.cos(a), y: 62 + 44 * Math.sin(a) };
      }),
    [],
  );
  return (
    <svg
      width={size}
      height={(size * height) / 120}
      viewBox={`0 0 120 ${height}`}
      fill="none"
      className={`shizi ${className}`}
      data-pose={pose}
      data-variant={full ? 'full' : 'head'}
      aria-hidden
    >
      <g transform={facing === 'left' ? 'translate(120 0) scale(-1 1)' : undefined}>
        {full && <Body pose={pose} />}
        <circle cx="60" cy="62" r="46" fill="#D98C1F" />
        {mane.map((p, i) => (
          <circle key={i} cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r="14.5" fill="#F2A93B" />
        ))}
        <circle cx="60" cy="62" r="38" fill="#FBD98F" />
        <circle cx="30" cy="34" r="9" fill="#F2A93B" />
        <circle cx="30" cy="34" r="4.5" fill="#F5B8A8" />
        <circle cx="90" cy="34" r="9" fill="#F2A93B" />
        <circle cx="90" cy="34" r="4.5" fill="#F5B8A8" />
        <circle cx="44" cy="66" r="6" fill="#F9C9B4" opacity="0.9" />
        <circle cx="76" cy="66" r="6" fill="#F9C9B4" opacity="0.9" />
        {pose === 'asleep' ? (
          <g className="eyes-shut">
            <path
              d="M44 57 Q49 61 54 57"
              stroke="#1E2433"
              strokeWidth="2.6"
              strokeLinecap="round"
            />
            <path
              d="M66 57 Q71 61 76 57"
              stroke="#1E2433"
              strokeWidth="2.6"
              strokeLinecap="round"
            />
          </g>
        ) : (
          <g className="eyes">
            <circle cx="49" cy="56" r="4.6" fill="#1E2433" />
            <circle cx="50.6" cy="54.4" r="1.5" fill="#fff" />
            <circle cx="71" cy="56" r="4.6" fill="#1E2433" />
            <circle cx="72.6" cy="54.4" r="1.5" fill="#fff" />
          </g>
        )}
        <path d="M55 66 Q60 62 65 66 Q60 73 55 66 Z" fill="#5B4636" />
        {pose === 'cheer' || pose === 'chomp' ? (
          <path d="M50 73 Q60 84 70 73 Z" fill="#5B4636" />
        ) : pose === 'open' ? (
          <ellipse cx="60" cy="78" rx="9" ry="7" fill="#5B4636" />
        ) : (
          <path
            d="M52 74 Q56 79 60 75 Q64 79 68 74"
            stroke="#5B4636"
            strokeWidth="2.6"
            strokeLinecap="round"
          />
        )}
        {pose === 'asleep' && (
          <g className="zzz" fill="#1E2433" fontWeight="800" fontFamily="var(--font-sans)">
            <text x="92" y="30" fontSize="11">
              z
            </text>
            <text x="100" y="20" fontSize="14">
              z
            </text>
            <text x="108" y="9" fontSize="17">
              z
            </text>
          </g>
        )}
      </g>
    </svg>
  );
}

/**
 * The body, drawn only for `variant="full"`. It lives below the head's own geometry (the head
 * is centred at 60,62 with the mane reaching y≈120), so adding it never moves or resizes the
 * face — every existing head-only call site renders exactly as before.
 */
function Body({ pose }: { pose: Pose }) {
  const walking = pose === 'walk';
  const holding = pose === 'hold' || pose === 'chomp' || pose === 'cheer';
  return (
    <g className="shizi-body">
      {/* tail, behind everything */}
      <g className="shizi-tail">
        <path
          d="M86 146 C 106 144, 114 130, 110 116"
          stroke="#D98C1F"
          strokeWidth="7"
          strokeLinecap="round"
          fill="none"
        />
        <circle cx="110" cy="112" r="8" fill="#F2A93B" />
      </g>
      {/* torso */}
      <path
        d="M40 104 C 30 122, 30 150, 40 162 C 50 172, 70 172, 80 162 C 90 150, 90 122, 80 104 Z"
        fill="#F2A93B"
      />
      <path
        d="M47 118 C 40 132, 40 150, 48 158 C 56 165, 64 165, 72 158 C 80 150, 80 132, 73 118 Z"
        fill="#FBD98F"
      />
      {/* front paws: forward when he is holding something out, otherwise resting */}
      <g className={walking ? 'shizi-paw-l' : undefined}>
        <ellipse cx={holding ? 38 : 44} cy={holding ? 132 : 160} rx="10" ry="8" fill="#FBD98F" />
      </g>
      <g className={walking ? 'shizi-paw-r' : undefined}>
        <ellipse cx={holding ? 82 : 76} cy={holding ? 132 : 160} rx="10" ry="8" fill="#FBD98F" />
      </g>
    </g>
  );
}

const COLORS = ['#F2A93B', '#1E7F95', '#35A96B', '#E2574C', '#FBD98F'];

/** A short burst of confetti; renders once and fades within ~1.5 s. */
export function Confetti({ count = 24 }: { count?: number }) {
  const bits = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        left: `${(i * 37) % 100}%`,
        delay: `${(i % 6) * 0.08}s`,
        color: COLORS[i % COLORS.length]!,
        size: 8 + (i % 3) * 3,
      })),
    [count],
  );
  return (
    <div
      className="confetti pointer-events-none absolute inset-x-0 top-0 h-40 overflow-visible"
      aria-hidden
    >
      {bits.map((b, i) => (
        <span
          key={i}
          style={{
            left: b.left,
            animationDelay: b.delay,
            background: b.color,
            width: b.size,
            height: b.size,
          }}
        />
      ))}
    </div>
  );
}
